import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sparkles, Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { celebrate } from "@/lib/celebrate";
import BackButton from "@/components/BackButton";

const PRO_FEATURES = [
  "Unlimited songs in your library",
  "Every practice mode: Play, Test, Perform",
  "AI Coach hints and Memory Map analysis",
  "Sing It voice grading with Whisper",
  "Backing tracks + speed control",
  "Weak-spot targeted drilling",
];

export default function Billing() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = async () => {
    try { const { data } = await api.get("/billing/status"); setStatus(data); }
    catch { /* keep status null; UI will show the free plan defaults */ }
  };
  useEffect(() => { load(); }, []);

  // Handle success/cancel returns from Stripe
  useEffect(() => {
    const sid = params.get("session_id");
    const canceled = params.get("canceled");
    if (canceled) {
      toast("Checkout canceled — no charge made.");
      setParams({}, { replace: true });
      return;
    }
    if (!sid) return;
    let cancelled = false;
    (async () => {
      for (let i = 0; i < 8 && !cancelled; i++) {
        try {
          const { data } = await api.get(`/billing/session/${sid}`);
          if (data.payment_status === "paid") {
            toast.success("You're on Pro. Welcome in.");
            celebrate("big");
            setParams({}, { replace: true });
            load();
            return;
          }
        } catch { /* keep polling */ }
        await new Promise((r) => setTimeout(r, 1800));
      }
      load();
    })();
    return () => { cancelled = true; };
  }, [params, setParams]);

  const upgrade = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Something went wrong starting checkout. Please try again.");
      setBusy(false);
    }
  };

  const manage = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch {
      toast.error("Couldn't open the billing portal. Please try again.");
      setBusy(false);
    }
  };

  const isPro = status?.plan === "pro";
  const usedPct = status ? Math.min(100, Math.round((status.song_count / status.song_limit) * 100)) : 0;

  return (
    <main data-testid="billing-page" className="max-w-3xl mx-auto px-6 md:px-10 py-14 md:py-20">
      <BackButton to="/library" label="Back to library" testid="billing-back-btn" />

      <header className="mt-8">
        <p className="overline-label text-accent">Billing</p>
        <h1 className="mt-3 font-display text-4xl md:text-6xl tracking-tight leading-[1.05]">
          {isPro ? "You're on Pro." : "Unlock every song."}
        </h1>
        <p className="mt-4 text-lg text-muted-foreground max-w-xl">
          {isPro
            ? "Full library, every drill, every mode — thanks for supporting the app."
            : `Free plan lets you save up to ${status?.song_limit || 3} songs so you can try every mode. Go Pro for your whole setlist.`}
        </p>
      </header>

      {/* Current plan card */}
      <section className="mt-10 grain-panel border border-border rounded-2xl bg-card p-6" data-testid="billing-current-plan">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="overline-label">Current plan</p>
            <p className="mt-2 font-display text-3xl">{isPro ? "Pro" : "Free"}</p>
            {!isPro && status && (
              <p className="mt-2 text-sm text-muted-foreground" data-testid="billing-usage">
                {status.song_count} of {status.song_limit} songs used
              </p>
            )}
            {isPro && status?.current_period_end && (
              <p className="mt-2 text-sm text-muted-foreground">
                {status.cancel_at_period_end ? "Cancels" : "Renews"} on{" "}
                {new Date(status.current_period_end * 1000).toLocaleDateString()}
              </p>
            )}
          </div>
          {!isPro && (
            <div className="w-full sm:w-48">
              <div className="h-2 rounded-full bg-secondary overflow-hidden">
                <div className={`h-full transition-all duration-500 ${usedPct >= 100 ? "bg-destructive" : "bg-primary"}`}
                     style={{ width: `${usedPct}%` }} />
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Pro card */}
      <section className="mt-6 grain-panel border border-primary/40 rounded-2xl p-7 bg-primary/5" data-testid="billing-pro-card">
        <div className="flex items-baseline gap-3">
          <span className="font-display text-5xl text-primary">$4.99</span>
          <span className="font-mono text-sm text-muted-foreground uppercase tracking-widest">/ month</span>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">Cancel any time from your billing page.</p>

        <ul className="mt-6 space-y-2.5">
          {PRO_FEATURES.map((f) => (
            <li key={f} className="flex items-start gap-2.5 text-sm">
              <Check size={16} className="text-primary mt-0.5 shrink-0" />
              <span>{f}</span>
            </li>
          ))}
        </ul>

        <div className="mt-8 flex items-center gap-3 flex-wrap">
          {isPro ? (
            <Button onClick={manage} disabled={busy} className="rounded-full h-12 px-6" data-testid="billing-manage-btn">
              <ExternalLink size={16} className="mr-1.5" /> Manage subscription
            </Button>
          ) : (
            <Button onClick={upgrade} disabled={busy}
                    className="rounded-full h-12 px-6 glow-amber-sm hover:glow-amber transition-shadow"
                    data-testid="billing-upgrade-btn">
              <Sparkles size={16} className="mr-1.5" /> {busy ? "Opening checkout…" : "Upgrade to Pro"}
            </Button>
          )}
          <Link to="/library" className="text-sm text-muted-foreground hover:text-foreground">
            Not now
          </Link>
        </div>
      </section>

      <p className="mt-8 text-xs text-muted-foreground">
        Payments processed securely by Stripe. Cancel any time from your billing portal.
      </p>
    </main>
  );
}
